import { observable } from "mobx";
import { Type } from "class-transformer";
import VehicleRequest from "./licensePlate";
import type { EntityValidationRules } from "services/validationService";
import { endOfCzechDay } from "utils/dateUtils";
import { addDays } from "date-fns";

// This entity has been generated, do not change its content, your changes might get lost. You CAN modify the rest of the file.
export class ExemptionChangeRequest {
  /**
   * Exemption ID.
   */
  @observable
  exemptionId?: string;

  /**
   * Creation record ID.
   */
  @observable
  creationRecordId?: string;

  /**
   * Record ID.
   */
  @observable
  officialRecordId?: string;

  /**
   * Record ID.
   */
  @observable
  _officialRecordId?: string;

  /**
   * Record ID.
   */
  @observable
  vehicleRegistrationChangeRecordId?: string;

  /**
   * Cancellation record ID.
   */
  @observable
  cancellationRecordId?: string;

  /**
   * Cancellation reason.
   */
  @observable
  cancellationReason?: string;

  /**
   * Exemption reason ID.
   */
  @observable
  exemptionReasonId!: string;

  @observable
  @Type(() => VehicleRequest)
  vehicle!: VehicleRequest;

  /**
   * Exemption is valid since this date and time.
   */
  @observable
  @Type(() => Date)
  validSince!: Date;

  /**
   * If true, exemption should be canceled immediately.
   */
  @observable
  isImmediateCancellation: boolean = false;

  /**
   * If specified, exemption is valid until this date and time.
   */
  @observable
  @Type(() => Date)
  validUntil?: Date;

  @observable
  @Type(() => Date)
  _originalValidUntil?: Date;

  @observable
  @Type(() => Date)
  _originalValidSince!: Date;
}

export default class ExemptionUpdateRequest extends ExemptionChangeRequest {
  constructor(exemption: ExemptionChangeRequest) {
    super();
    this.exemptionId = exemption.exemptionId;
    this.creationRecordId = exemption.creationRecordId;
    this.cancellationRecordId = exemption.cancellationRecordId;
    this.cancellationReason = exemption.cancellationReason;
    this.officialRecordId = exemption.officialRecordId;
    this.vehicleRegistrationChangeRecordId = exemption.vehicleRegistrationChangeRecordId;
    this.exemptionReasonId = exemption.exemptionReasonId;
    this.vehicle = exemption.vehicle;
    this.validSince = exemption.validSince;
    this.validUntil = exemption.validUntil;
    this._originalValidUntil = exemption.validUntil;
    this._originalValidSince = exemption.validSince;
  }
}

export const UpdateExemptionValidationRules: EntityValidationRules<ExemptionChangeRequest> = {
  exemptionReasonId: {
    required: true,
  },
  validSince: {
    required: true,
    isAfter: {
      condition: (entity: ExemptionChangeRequest) =>
        entity._originalValidSince > new Date()
          ? endOfCzechDay(addDays(new Date(), -1))
          : endOfCzechDay(addDays(entity._originalValidSince, -1)),
    },
    isBefore: {
      condition: (entity: ExemptionChangeRequest) =>
        entity.isImmediateCancellation && entity.validUntil ? entity.validUntil : endOfCzechDay(entity.validSince),
    },
  },
  validUntil: {
    isAfter: {
      condition: (entity: ExemptionChangeRequest) =>
        entity.isImmediateCancellation ||
        entity.validSince > new Date() ||
        (entity._originalValidUntil && entity._originalValidUntil < new Date() && entity._originalValidSince < new Date())
          ? endOfCzechDay(addDays(entity.validSince, -1))
          : endOfCzechDay(addDays(new Date(), -1)),
    },
  },
  creationRecordId: {
    required: true,
    isLength: {
      min: 3,
      max: 50,
    },
  },
  cancellationRecordId: {
    requiredIf: {
      condition: (entity: ExemptionChangeRequest) => !!entity.validUntil,
    },
    isLength: {
      optional: true,
      min: 3,
      max: 50,
      reverseMax: 0,
      reverseProperty: "validUntil",
    },
  },
  cancellationReason: {
    isLength: {
      optional: true,
      max: 160,
      reverseMax: 0,
      reverseProperty: "validUntil",
    },
  },
};
